import { ProjectIntegrationProviderName } from "@/store/integrations/integrations-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { PointCloudType } from "@faro-lotv/project-source";

/**
 * Properties of openModel3dView which tracks the type in which the user is viewing the models
 */
export enum OpenModel3dViewProperties {
  scan = "scan",
  design = "design",
  pointcloud = "pointcloud",
}

/**
 * Properties of selectViaProjectOverview which tracks the item that has been clicked from the project overview
 */
export enum SelectViaProjectOverviewProperties {
  sheet = "sheet",
  location = "location",
  capture = "capture",
}

/**
 * Properties of location selection tracking which contains the information on where the event originated from
 */
export enum SelectLocationSources {
  minimap = "minimap",
  sheetView = "sheetView",
  projectView = "projectView",
  panoView = "panoView",
  panoViewWaypointClick = "panoView_waypointClick",
  panoViewPanoClick = "panoView_panoClick",
  model3dView = "3dView",
}

export type SelectLocationProperties = {
  /** From which place the user selected a specific location */
  via: SelectLocationSources;
  /** Which tool was active while the user changed the location */
  tool: ReturnType<typeof computeToolPropertyForSelectLocationEvent>;
};

/**
 * @returns The string to use for the "tool" property of the "select location" event
 * @param tool The tool active when the event is triggered
 */
export function computeToolPropertyForSelectLocationEvent(
  tool: ToolName | null,
): "none" | ToolName.createPDFReport | ToolName.createSnapshot {
  if (tool === ToolName.createPDFReport || tool === ToolName.createSnapshot) {
    return tool;
  }
  return "none";
}

/**
 * Source types which contains the information on where the event originated from
 * i.e from where the 360 image or a capture was selected
 */
export enum SelectCaptureSources {
  projectOverview = "projectOverview",
  dropdown = "dropdown",
  splitscreen = "splitscreen",
  carousel = "carousel",
  gallery = "gallery",
}

/**
 * Properties of selectCaptureProperties which tracks where and what capture the user is viewing
 */
export type SelectCaptureProperties = {
  /** source of the event */
  via: SelectCaptureSources;

  /** capture types */
  type: "360" | "2D" | "2D object";
};

/**
 * Properties of inspectCaptureLocation which tracks the type in which the user is viewing the 360s
 */
export type InspectCaptureLocationProperties = {
  type: "panoView" | "pointcloud";
};

// ------- PointCloud Management events

/**
 * Properties for the selectPointCloudFile event
 */
export type SelectPointCloudFileEventProperties = {
  /** Size of the file in bytes */
  fileSize: number;

  /** File extension */
  extension: string;
};

/**
 * Properties for the uploadPointCloud event
 */
export type UploadPointCloudEventProperties =
  SelectPointCloudFileEventProperties & {
    /** Number of Point Clouds already existing on the floor */
    numberOfExistingPCs: number;

    /** Indicator if the user changed the name of the PC before upload */
    nameChanged: boolean;

    /** Indicator if the user changed the time point of the PC before upload */
    timePointChanged: boolean;

    /** Indicator if the user changed the preselected floor of the PC before upload */
    floorChanged: boolean;
  };

/**
 * Properties for the alignPointCloud event
 */
export type AlignPointCloudEventProperties = {
  /** Which UI was used to start the alignment tool */
  via: "context menu" | "align button";

  /** Cumber of PCs that are already aligned to the floor */
  numberOfAlignedPCs: number;

  /** Indicating if the user re-aligns a PC */
  alreadyAligned: boolean;
};

/** Properties for the switchPointCloudStream event */
export type SwitchPointCloudStreamEventProperties = {
  /** The type of the selected point cloud stream */
  pointCloudType: PointCloudType;
};

/** Properties for the downloadImage event */
export type DownloadImageProperties = {
  /** The type of image  */
  imageType: "360" | "2D";
};

/** Properties for import area and sheet events */
export type ImportAreaSheetProperties = {
  /** Number of selected pages */
  numberOfSheetsSelected: number;

  /** Total number of pages in document */
  numberOfSheetsInDocument: number;

  /** True if crop is applied */
  isCropActive: boolean;

  /** True if rotation is applied */
  isRotationActive: boolean;

  /** Type of selected file for import */
  fileType: string;

  /** Indicates if an improved resolution has been requested */
  isImprovedResolution: boolean;
};

/** Properties for upload360Photo and upload2DPhoto event*/
export type UploadImageProperties = {
  /** The number of images to upload  */
  numberOfImages: number;

  /** Whether a new waypoint was created for the uploaded images */
  isNewWaypoint: boolean;
};

/** Properties for the downloadImage event */
export type TimeseriesDropdownStyleProperty = {
  /** The type of image  */
  style: "text list" | "gallery";
};

/** Properties for the authorizeExternalAnnotation event */
export type AuthorizeExternalAnnotationProperties = {
  /** Indicates the source of the authorization event */
  via:
    | "callout"
    | "creation dialog"
    | "annotations filter"
    | "error toast message";
  /** The external annotation provider service for which the authorization event occurred */
  service: ProjectIntegrationProviderName;
};
